import React, { useState, useEffect, useRef, useMemo } from "react";
import Layout from "../Layout/Layout";
import { ChatSend, ChatReceive } from "../Components/Chat/ChatBody";
import { MessageBox } from "../Components/Chat/ChatInput";
import { API_BASE } from "../Utils/constant";
import axios from "axios";
import moment from "moment";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { store } from "../store";
import { resetAlert, setAlert } from "../store/features/alertSlice";
import { useSelector } from "react-redux"; // still used for alert slice
import useUserStore from "../zustand-stores/UserStore";
import { usePromptWithModal } from "../hooks/usePromptWithModal";
import { AUTH_TOKEN } from "../constants/localStorageKeys";
import { retrieve } from "../helpers/StorageHelper";
import { getUserTimezone } from "../helpers/TimezoneHelper";

export default function Journal() {
  const { emotion } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [userJournalId, setUserJournalId] = useState(() => {
    return location.state?.id || null;
  });

  const { user: userData } = useUserStore();

  const [emotionName, setEmotionName] = useState("");
  const [emotionId, setEmotionId] = useState("");
  const [journalData, setJournalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [completedMessage, setCompletedMessage] = useState("");

  const [journalingBegun, setJournalingBegun] = useState(false);

  const [showYesNoModal, setShowYesNoModal] = useState(false);
  const [currentYesNoQuestion, setCurrentYesNoQuestion] = useState(null);

  //edit
  const [editingMessage, setEditingMessage] = useState(null);

  const { alertState, type } = useSelector((state) => state.alert);
  const previousEmotionIdRef = useRef(null);

  const {
    showModal: showConfirmModal,
    message,
    proceed,
    cancel,
  } = usePromptWithModal(alertState, "Do you really want to leave this page?");

  const chatContainerRef = useRef(null);
  const [isThinkingLoading, setIsThinkingLoading] = useState(false);

  /** localStorage draft: emotion/journal type (route or id) + session + question (main vs follow-up) */
  const journalComposerDraftScopeId = useMemo(() => {
    if (!currentQuestion?.id || editingMessage) {
      return undefined;
    }
    const session = userJournalId ?? "new";
    const qKind = currentQuestion.isFollowUp ? "followup" : "main";
    const typeKey = emotion || emotionId || "unknown";
    return `journal:${typeKey}:uj:${session}:q:${currentQuestion.id}:${qKind}`;
  }, [
    emotion,
    emotionId,
    userJournalId,
    currentQuestion?.id,
    currentQuestion?.isFollowUp,
    editingMessage,
  ]);

  const createApiConfig = (token, includeUserJournalId = true) => {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        time_zone: getUserTimezone(),
      },
    };

    if (includeUserJournalId && userJournalId) {
      config.params = {
        user_journal_id: userJournalId,
      };
    }

    return config;
  };

  // handleJournalCompletion
  const handleJournalCompletion = () => {
    setIsThinkingLoading(true);

    setAllQuestionsAnswered(true);
    setCurrentQuestion(null);

    // Add completion message
    setMessages((prev) => [
      ...prev,
      {
        type: "send",
        question: "Thank you for completing today's journal entry.",
        time: moment().format("h:mm A"),
        botImage: "/icons/journal-brave.svg",
      },
    ]);

    setTimeout(() => {
      setIsThinkingLoading(false);
    }, 1000);

    setTimeout(() => {
      navigate("/journal-library");
    }, 3000);
  };

  // Get emotion data from localStorage
  useEffect(() => {
    const storedEmotions = retrieve("emotions");

    if (storedEmotions && storedEmotions.length > 0) {
      const formattedEmotionParam = emotion?.toLowerCase().replace(/-/g, " ");

      const matchedEmotion = storedEmotions.find(
        (item) =>
          item.id.toString() === emotion ||
          item.emotion_name.toLowerCase().replace(/-/g, " ") ===
            formattedEmotionParam,
      );

      if (matchedEmotion) {
        setEmotionName(matchedEmotion.emotion_name);
        setEmotionId(matchedEmotion.id);
      } else {
        console.warn(`Emotion "${emotion}" not found in stored emotions.`);

        const firstEmotion = storedEmotions[0];
        setEmotionName(firstEmotion.emotion_name);
        setEmotionId(firstEmotion.id);
      }
    } else {
    }
  }, [emotion]);

  const initializeChat = (journal) => {
    setIsThinkingLoading(true);

    const chatMessages = [];
    let nextQuestion = null;
    let lastAnswerTime = null;

    // Process each main question in order
    for (const mainQuestion of journal.main_questions) {
      // Add the main question if it's answered
      if (mainQuestion.answered && mainQuestion.answer) {
        const questionTime = moment(mainQuestion.answer.created_at);
        chatMessages.push({
          type: "send",
          question: mainQuestion.question,
          time: questionTime.format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
        });

        lastAnswerTime = moment(mainQuestion.answer.created_at);
        chatMessages.push({
          type: "receive",
          response: mainQuestion.answer.text,
          time: lastAnswerTime.format("h:mm A"),
          responder: userData?.full_name || "User",
          userImage: userData?.image || "/icons/toni.svg",
          answerId: mainQuestion.answer.id,
          questionType: mainQuestion.question_type,
        });

        // Process follow-up questions only if the main question is answered
        if (
          mainQuestion.follow_up_questions &&
          mainQuestion.follow_up_questions.length > 0
        ) {
          for (const followUpQuestion of mainQuestion.follow_up_questions) {
            if (followUpQuestion.answered && followUpQuestion.answer) {
              const questionTime = moment(followUpQuestion.answer.created_at);
              chatMessages.push({
                type: "send",
                question: followUpQuestion.question,
                time: questionTime.format("h:mm A"),
                botImage: "/icons/journal-brave.svg",
              });

              lastAnswerTime = moment(followUpQuestion.answer.created_at);
              chatMessages.push({
                type: "receive",
                response: followUpQuestion.answer.text,
                time: lastAnswerTime.format("h:mm A"),
                responder: userData?.full_name || "User",
                userImage: userData?.image || "/icons/toni.svg",
                answerId: followUpQuestion.answer.id,
                questionType: followUpQuestion.question_type,
              });
            } else if (!nextQuestion) {
              // Set the first unanswered follow-up as next question
              nextQuestion = {
                ...followUpQuestion,
                isFollowUp: true,
                mainQuestionId: mainQuestion.id,
              };
              break;
            }
          }
        }
      } else if (!nextQuestion) {
        // Set the first unanswered main question as next question
        nextQuestion = { ...mainQuestion, isFollowUp: false };
        break;
      }

      // If we found the next question, break the loop
      if (nextQuestion) {
        break;
      }
    }

    // Check if all questions are answered
    const allMainAnswered = journal.main_questions.every((q) => q.answered);
    const allFollowUpAnswered = journal.main_questions.every(
      (mainQ) =>
        !mainQ.follow_up_questions ||
        mainQ.follow_up_questions.every((followUp) => followUp.answered),
    );

    setAllQuestionsAnswered(allMainAnswered && allFollowUpAnswered);

    // Add the next unanswered question (if any)
    if (nextQuestion) {
      const questionTime = lastAnswerTime || moment();
      chatMessages.push({
        type: "send",
        question: nextQuestion.question,
        time: questionTime.format("h:mm A"),
        botImage: "/icons/journal-brave.svg",
      });

      if (nextQuestion.question_type === "yes_no") {
        setCurrentYesNoQuestion(nextQuestion);
        setShowYesNoModal(true);
        setTimeout(() => {
          setIsThinkingLoading(false);
        }, 2000);
      } else {
        setTimeout(() => {
          setIsThinkingLoading(false);
        }, 2000);
      }
    } else {
      setIsThinkingLoading(false);
    }

    // If there are any messages, we consider journaling to have begun
    if (chatMessages.length > 1) {
      setJournalingBegun(true);
    }

    setMessages(chatMessages);
    setCurrentQuestion(nextQuestion);
  };

  useEffect(() => {
    if (emotionId && previousEmotionIdRef.current !== emotionId) {
      previousEmotionIdRef.current = emotionId;

      setJournalingBegun(false);
      setLoading(true);
      const token = retrieve(AUTH_TOKEN);
      const config = createApiConfig(token);

      axios
        .get(`${API_BASE}/journals/${emotionId}`, config)
        .then((response) => {
          setJournalData(response.data.data.journal);
          initializeChat(response.data.data.journal);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching journal:", err);

          // Check if the error is due to already completed journal
          if (
            err.response &&
            err.response.data &&
            err.response.data.status === false
          ) {
            setCompletedMessage(err.response.data.message);
            // Redirect after 3 seconds
            setTimeout(() => {
              navigate("/journal-library");
            }, 3000);
          } else {
            setError("Failed to fetch journal data");
            toast.error("Failed to fetch journal data");
          }

          setLoading(false);
        });
    }
  }, [emotionId, navigate, userJournalId]);

  const handleSendMessage = async (messageText) => {
    if (editingMessage) {
      await handleSaveEdit(editingMessage.messageId, messageText);
      return;
    }

    // Clean the message text by removing HTML tags if any
    const cleanedMessage = messageText.trim();

    if (!cleanedMessage || !journalData || !currentQuestion) {
      console.error("Invalid message text or missing required data");
      toast.error("Enter message");
      return;
    }

    if (currentQuestion.question_type === "yes_no") {
      return;
    }

    const token = retrieve(AUTH_TOKEN);
    const currentTime = moment().format("h:mm A");

    // Add user's message to chat immediately for better UX
    const newUserMessage = {
      type: "receive",
      response: cleanedMessage,
      time: currentTime,
      responder: userData?.full_name || "User",
      userImage: userData?.image || "/icons/toni.svg",
    };

    setMessages((prev) => [...prev, newUserMessage]);

    // Prepare data for API
    const payload = {
      journal_id: journalData.id,
      text: cleanedMessage,
    };

    // Determine if it's a main or follow-up question
    if (currentQuestion.isFollowUp) {
      payload.follow_up_question_id = currentQuestion.id;
    } else {
      payload.main_question_id = currentQuestion.id;
    }

    try {
      const config = createApiConfig(token);

      // Submit the answer to the API
      const answerResponse = await axios.post(
        `${API_BASE}/journal-answers`,
        payload,
        config,
      );

      const updatedUserJournalId = answerResponse.data.data.user_journal_id;
      setUserJournalId(updatedUserJournalId);

      try {
        // Fetch updated journal data with the updated userJournalId
        const journalConfig = createApiConfig(token);
        // Use the fresh userJournalId from API response
        if (updatedUserJournalId) {
          journalConfig.params = {
            user_journal_id: updatedUserJournalId,
          };
        }

        const journalResponse = await axios.get(
          `${API_BASE}/journals/${emotionId}`,
          journalConfig,
        );

        if (!journalResponse.data?.data?.journal) {
          throw new Error("Invalid journal data received");
        }

        const updatedJournal = journalResponse.data.data.journal;
        setJournalData(updatedJournal);

        // Check if all questions are answered
        const allMainAnswered =
          updatedJournal.main_questions?.every((q) => q.answered) || false;
        const allFollowUpAnswered =
          updatedJournal.main_questions?.every(
            (mainQ) =>
              !mainQ.follow_up_questions ||
              mainQ.follow_up_questions.every((followUp) => followUp.answered),
          ) || false;

        const allAnswered = allMainAnswered && allFollowUpAnswered;

        if (allAnswered) {
          handleJournalCompletion();
        } else {
          initializeChat(updatedJournal);
        }
      } catch (fetchErr) {
        console.error("Error fetching updated journal data:", fetchErr);

        // Fallback: Handle with local data
        try {
          const currentQuestionId = currentQuestion.id;
          const isFollowUp = currentQuestion.isFollowUp;
          const updatedLocalJournal = { ...journalData };

          // Mark the current question as answered in our local copy
          if (isFollowUp) {
            // Find the main question that contains this follow-up
            const mainQIndex = updatedLocalJournal.main_questions.findIndex(
              (mainQ) =>
                mainQ.follow_up_questions?.some(
                  (fq) => fq.id === currentQuestionId,
                ),
            );

            if (mainQIndex !== -1) {
              const followUpIndex = updatedLocalJournal.main_questions[
                mainQIndex
              ].follow_up_questions.findIndex(
                (fq) => fq.id === currentQuestionId,
              );

              if (followUpIndex !== -1) {
                updatedLocalJournal.main_questions[
                  mainQIndex
                ].follow_up_questions[followUpIndex].answered = true;
                updatedLocalJournal.main_questions[
                  mainQIndex
                ].follow_up_questions[followUpIndex].answer = {
                  id: Date.now(), // temporary ID
                  text: cleanedMessage,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                };
              }
            }
          } else {
            const questionIndex = updatedLocalJournal.main_questions.findIndex(
              (q) => q.id === currentQuestionId,
            );

            if (questionIndex !== -1) {
              updatedLocalJournal.main_questions[questionIndex].answered = true;
              updatedLocalJournal.main_questions[questionIndex].answer = {
                id: Date.now(), // temporary ID
                text: cleanedMessage,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              };
            }
          }

          // Check completion based on local data
          const allMainAnsweredLocally =
            updatedLocalJournal.main_questions.every((q) => q.answered);
          const allFollowUpAnsweredLocally =
            updatedLocalJournal.main_questions.every(
              (mainQ) =>
                !mainQ.follow_up_questions ||
                mainQ.follow_up_questions.every(
                  (followUp) => followUp.answered,
                ),
            );

          if (allMainAnsweredLocally && allFollowUpAnsweredLocally) {
            handleJournalCompletion();
          } else {
            // Initialize chat with updated local data
            initializeChat(updatedLocalJournal);
          }
        } catch (localErr) {
          console.error("Error handling with local data:", localErr);
          setMessages((prev) => [
            ...prev,
            {
              type: "send",
              question: "Sorry, something went wrong. Please try again.",
              time: moment().format("h:mm A"),
              botImage: "/icons/journal-brave.svg",
            },
            {
              type: "send",
              question: currentQuestion.question,
              time: moment().format("h:mm A"),
              botImage: "/icons/journal-brave.svg",
            },
          ]);
        }
      }
    } catch (err) {
      console.error("Error submitting journal answer:", err);

      // Handle specific error cases
      if (err.response?.data?.status === false) {
        // Journal might be completed
        try {
          const completionCheck = await axios.get(
            `${API_BASE}/journals/${emotionId}`,
            createApiConfig(retrieve(AUTH_TOKEN)),
          );

          if (completionCheck.data?.data?.journal) {
            const journal = completionCheck.data.data.journal;
            const allMainAnswered =
              journal.main_questions?.every((q) => q.answered) || false;
            const allFollowUpAnswered =
              journal.main_questions?.every(
                (mainQ) =>
                  !mainQ.follow_up_questions ||
                  mainQ.follow_up_questions.every(
                    (followUp) => followUp.answered,
                  ),
              ) || false;

            if (allMainAnswered && allFollowUpAnswered) {
              handleJournalCompletion();
              return;
            }
          }
        } catch (checkErr) {
          console.error("Failed to check completion status:", checkErr);
        }
      }

      // If we get here, show a generic error and retry the question
      setMessages((prev) => [
        ...prev,
        {
          type: "send",
          question: "Sorry, something went wrong. Please try again.",
          time: moment().format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
        },
        {
          type: "send",
          question: currentQuestion.question,
          time: moment().format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
        },
      ]);
    }
  };

  const handleStartEdit = (answerId, currentText) => {
    if (!answerId) {
      console.error("No answer ID provided for edit");
      toast.error("Cannot edit this message - missing ID");
      return;
    }

    // Find the message being edited
    const messageToEdit = messages.find(
      (msg) => msg.type === "receive" && msg.answerId === answerId,
    );

    if (!messageToEdit) {
      console.error("Message to edit not found");
      toast.error("Cannot find the message to edit");
      return;
    }

    // Handle yes/no answers differently
    if (messageToEdit.questionType === "yes_no") {
      // Find the corresponding question in journalData
      let questionToEdit = null;

      // Search through main questions
      for (const mainQ of journalData.main_questions) {
        if (mainQ.answer?.id === answerId) {
          questionToEdit = { ...mainQ, isFollowUp: false };
          break;
        }

        // Search through follow-up questions
        if (mainQ.follow_up_questions) {
          for (const followUpQ of mainQ.follow_up_questions) {
            if (followUpQ.answer?.id === answerId) {
              questionToEdit = {
                ...followUpQ,
                isFollowUp: true,
                mainQuestionId: mainQ.id,
              };
              break;
            }
          }
        }
        if (questionToEdit) break;
      }

      if (questionToEdit) {
        setCurrentYesNoQuestion(questionToEdit);
        setShowYesNoModal(true);
        // Store the answer ID we're editing
        setEditingMessage({
          messageId: answerId,
          currentText,
          isYesNo: true,
        });
      } else {
        toast.error("Could not find the question to edit");
      }
    } else {
      // Regular text answer
      setEditingMessage({ messageId: answerId, currentText });
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
  };

  const handleSaveEdit = async (answerId, newText) => {
    if (!answerId) {
      console.error("No answer ID provided for edit");
      toast.error("Cannot save edit - missing message ID");
      return;
    }

    const token = retrieve(AUTH_TOKEN);

    try {
      const cleanedText = newText.trim();

      const response = await axios.post(
        `${API_BASE}/journal-answers/${answerId}`,
        { text: cleanedText },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const journalConfig = createApiConfig(token);
      const journalResponse = await axios.get(
        `${API_BASE}/journals/${emotionId}`,
        journalConfig,
      );

      setJournalData(journalResponse.data.data.journal);
      initializeChat(journalResponse.data.data.journal);
      setEditingMessage(null);
      toast.success("Answer updated successfully");
    } catch (err) {
      console.error("Error updating answer:", err);
      toast.error("Failed to update answer");

      // Check if the error is because the answer doesn't exist
      if (err.response?.status === 404) {
        toast.error("The message you're trying to edit no longer exists");
      }
    }
  };

  useEffect(() => {
    if (!allQuestionsAnswered) {
      if (messages.length) {
        store.dispatch(setAlert("jounral_ongoing"));
      }
    } else {
      store.dispatch(resetAlert());
    }
  }, [messages, allQuestionsAnswered]);

  const handleBeginJournaling = () => {
    setJournalingBegun(true);

    if (journalData?.main_questions?.length > 1) {
      initializeChat(journalData);
    }
  };

  const handleYesNoResponse = async (response) => {
    const messageText = response ? "yes" : "no";
    const currentTime = moment().format("h:mm A");

    // Check if we're editing an existing answer
    const isEditing = editingMessage?.isYesNo;
    const answerId = isEditing ? editingMessage.messageId : Date.now();

    // Add/update user's message in chat
    const updatedMessages = [...messages];
    const messageIndex = isEditing
      ? updatedMessages.findIndex(
          (msg) => msg.type === "receive" && msg.answerId === answerId,
        )
      : -1;

    const newUserMessage = {
      type: "receive",
      response: messageText,
      time: currentTime,
      responder: userData?.full_name || "User",
      userImage: userData?.image || "/icons/toni.svg",
      answerId,
      questionType: currentYesNoQuestion.question_type,
    };

    if (messageIndex !== -1) {
      // Replace the existing message
      updatedMessages[messageIndex] = newUserMessage;
    } else {
      // Add new message
      updatedMessages.push(newUserMessage);
    }

    setMessages(updatedMessages);
    setShowYesNoModal(false);
    if (isEditing) setEditingMessage(null);

    // Prepare API payload
    const token = retrieve(AUTH_TOKEN);
    const payload = {
      journal_id: journalData.id,
      text: messageText,
    };

    if (currentYesNoQuestion.isFollowUp) {
      payload.follow_up_question_id = currentYesNoQuestion.id;
    } else {
      payload.main_question_id = currentYesNoQuestion.id;
    }

    try {
      let answerResponse;

      if (isEditing) {
        // Update existing answer (no userJournalId needed for updates)
        answerResponse = await axios.post(
          `${API_BASE}/journal-answers/${answerId}`,
          { text: messageText },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      } else {
        const config = createApiConfig(token);
        answerResponse = await axios.post(
          `${API_BASE}/journal-answers`,
          payload,
          config,
        );

        // Update userJournalId from response
        const updatedUserJournalId = answerResponse.data.data.user_journal_id;
        setUserJournalId(updatedUserJournalId);
      }

      // Update the message with the real answer ID if it was a new answer
      if (!isEditing) {
        const realAnswerId = answerResponse.data.data.id;
        setMessages((prev) => {
          const updated = [...prev];
          const lastMessage = updated[updated.length - 1];
          if (lastMessage && lastMessage.type === "receive") {
            updated[updated.length - 1] = {
              ...lastMessage,
              answerId: realAnswerId,
            };
          }
          return updated;
        });
      }

      const journalConfig = createApiConfig(token);
      const journalResponse = await axios.get(
        `${API_BASE}/journals/${emotionId}`,
        journalConfig,
      );

      const updatedJournal = journalResponse.data.data.journal;
      setJournalData(updatedJournal);

      // Check if all questions are answered
      const allMainAnswered = updatedJournal.main_questions.every(
        (q) => q.answered,
      );
      const allFollowUpAnswered = updatedJournal.main_questions.every(
        (mainQ) =>
          !mainQ.follow_up_questions ||
          mainQ.follow_up_questions.every((followUp) => followUp.answered),
      );

      if (allMainAnswered && allFollowUpAnswered && !isEditing) {
        handleJournalCompletion();
      } else {
        initializeChat(updatedJournal);
      }
    } catch (err) {
      console.error("Error submitting yes/no answer:", err);
      toast.error("Error submitting yes/no answer.");

      // Revert the message if editing
      if (isEditing) {
        setMessages(messages); // Revert to original messages
      } else {
        // Remove the failed message
        setMessages((prev) => prev.slice(0, -1));
      }

      // Show error and retry
      setMessages((prev) => [
        ...prev,
        {
          type: "send",
          question: "Sorry, something went wrong. Please try again.",
          time: moment().format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
        },
        {
          type: "send",
          question: currentYesNoQuestion.question,
          time: moment().format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
        },
      ]);

      setShowYesNoModal(true);
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, isThinkingLoading]);

  const handleShowYesNoModal = (answerId, currentText) => {
    // Find the question associated with this answer
    let questionToEdit = null;

    // Search through main questions
    for (const mainQ of journalData.main_questions) {
      if (mainQ.answer?.id === answerId) {
        questionToEdit = { ...mainQ, isFollowUp: false };
        break;
      }

      // Search through follow-up questions
      if (mainQ.follow_up_questions) {
        for (const followUpQ of mainQ.follow_up_questions) {
          if (followUpQ.answer?.id === answerId) {
            questionToEdit = {
              ...followUpQ,
              isFollowUp: true,
              mainQuestionId: mainQ.id,
            };
            break;
          }
        }
      }
      if (questionToEdit) break;
    }

    if (questionToEdit) {
      setCurrentYesNoQuestion(questionToEdit);
      setShowYesNoModal(true);
      setEditingMessage({
        messageId: answerId,
        currentText,
        isYesNo: true,
      });
    } else {
      toast.error("Could not find the question to edit");
    }
  };

  return (
    <Layout>
      <div className="page-content">
        <div className="journal-chat-content">
          <div className="d-flex justify-content-between flex-column flex-sm-row">
            <h1 className="pages-header text-heading-h2">{emotionName}</h1>
            <div className="goto-btn">
              <button
                className="btn-1 btn-1-primary text-body-b3"
                onClick={() => (window.location.href = "/journal-library")}
                style={{
                  maxWidth: "205px",
                  width: "100%",
                  maxHeight: "39px",
                  height: "100%",
                }}
              >
                Go to journal library
              </button>
            </div>
          </div>
          <div className="d-flex flex-column goals-chat-card-container">
            {loading && (
              <div className="loader-container">
                <div className="loader"></div>
              </div>
            )}
            {error && <p>{error}</p>}

            <div
              ref={chatContainerRef}
              className="d-flex flex-column no-scrollbar"
              style={{
                flexGrow: 1,
                overflowX: "hidden",
                minHeight: 0,
                height: 0,
                flexShrink: 0,
              }}
            >
              {/* Render chat messages */}
              {messages.map((message, index) =>
                message.type === "send" ? (
                  <ChatSend
                    key={`send-${index}`}
                    username="Empowered Man"
                    title={message.question}
                    time={message.time}
                    botImage="/icons/journal-brave.svg"
                    setIsThinkingLoading={setIsThinkingLoading}
                    isThinkingLoading={isThinkingLoading}
                    isCurrentQuestion={
                      index === messages.length - 1 &&
                      message.type === "send" &&
                      index > 0
                    }
                  />
                ) : (
                  <ChatReceive
                    key={`receive-${index}`}
                    response={message.response}
                    responder={message.responder}
                    time={message.time}
                    userImage={message.userImage}
                    answerId={message.answerId}
                    questionType={message.questionType}
                    isEditing={editingMessage?.messageId === message.answerId}
                    onEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    disableEdit={message.questionType === "yes_no"}
                    onShowYesNoModal={handleShowYesNoModal}
                  />
                ),
              )}

              {/* Show begin button if no messages yet */}
              {messages.length === 1 &&
                !loading &&
                !error &&
                !journalingBegun && (
                  <button
                    className="text-body-b3 btn-1 btn-1-primary"
                    id="journal-button"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "240px",
                      height: "52px",
                      cursor: "pointer",
                      alignSelf: "center",
                      flexShrink: 0,
                    }}
                    onClick={handleBeginJournaling}
                  >
                    Begin Journaling
                  </button>
                )}
            </div>

            {/* Message Box - only show if there are unanswered questions */}
            {!showYesNoModal &&
            ((currentQuestion &&
              !allQuestionsAnswered &&
              journalingBegun &&
              currentQuestion.question_type !== "yes_no" &&
              !editingMessage) ||
              (editingMessage && !editingMessage.isYesNo)) ? (
              <MessageBox
                onSendClick={handleSendMessage}
                initialText={editingMessage?.currentText || ""}
                isEditing={!!editingMessage}
                setEditingMessage={setEditingMessage}
                userProfile={userData}
                draftScopeId={journalComposerDraftScopeId}
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* Yes/No Question Modal */}
      {showYesNoModal && currentYesNoQuestion && !isThinkingLoading && (
        <div
          className="modal fade not-completed-journal-modal show"
          id="notCompletedJournalModal"
          tabIndex="-1"
          aria-labelledby="notCompletedJournalModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header position-relative">
                <h1
                  className="modal-title"
                  id="notCompletedJournalModalLabel"
                ></h1>
              </div>

              <div className="modal-body">
                <p
                  className="text-body-b2 mb-4 text-wrap"
                  style={{ fontSize: "19px" }}
                >
                  {currentYesNoQuestion.question}
                </p>
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", height: "auto" }}
                    onClick={() => handleYesNoResponse(true)}
                  >
                    Yes
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px", height: "auto" }}
                    onClick={() => handleYesNoResponse(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="modal fade not-completed-journal-modal show"
          id="notCompletedJournalModal"
          tabIndex="-1"
          aria-labelledby="notCompletedJournalModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header position-relative">
                <button
                  type="button"
                  className="btn-close btn-close-white position-absolute"
                  style={{ top: "14px", right: "0px", color: "#e6ecf1" }}
                  aria-label="Close"
                  onClick={cancel}
                ></button>
                <h1 className="modal-title" id="notCompletedJournalModalLabel">
                  You have not completed
                  <br />
                  this journal
                </h1>
              </div>

              <div className="modal-body">
                <p className="text-body-b2 mb-4">
                  Do you want to leave this page?
                </p>
                <p className="text-body-b2 mb-0">
                  You can continue this journal from <br />
                  dashboard or journal library
                </p>
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", background: "#db4545" }}
                    onClick={() => {
                      store.dispatch(resetAlert());
                      proceed();
                    }}
                  >
                    Yes
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px" }}
                    onClick={cancel}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
